import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { api } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Calendar, Search, Filter, Download, FileSpreadsheet, ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react';
import { useSortableData } from '../hooks/useSortableData';
import Pagination from './Pagination';
const Snapshots = () => {
    const { user } = useAuth();
    const [snapshots, setSnapshots] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filter States (UI Inputs)
    const [searchTerm, setSearchTerm] = useState('');
    const [searchBySKU, setSearchBySKU] = useState(true);
    const [searchByProduct, setSearchByProduct] = useState(true);
    const [selectedClient, setSelectedClient] = useState('All');
    const [selectedWarehouse, setSelectedWarehouse] = useState('All');
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedDays, setSelectedDays] = useState(7);

    // Applied Filters for Fetching
    const [appliedFilters, setAppliedFilters] = useState({
        searchTerm: '',
        searchBySKU: true,
        searchByProduct: true,
        selectedClient: 'All',
        selectedWarehouse: 'All',
        selectedDate: '',
        selectedDays: 7
    });

    const handleFetch = () => {
        setAppliedFilters({ searchTerm, searchBySKU, searchByProduct, selectedClient, selectedWarehouse, selectedDate, selectedDays });
        setCurrentPage(1);
    };

    const handleClearAll = () => {
        setSearchTerm('');
        setSearchBySKU(true);
        setSearchByProduct(true);
        setSelectedClient('All');
        setSelectedWarehouse('All');
        setSelectedDate('');
        setSelectedDays(7);
        setAppliedFilters({
            searchTerm: '',
            searchBySKU: true,
            searchByProduct: true,
            selectedClient: 'All',
            selectedWarehouse: 'All',
            selectedDate: '',
            selectedDays: 7
        });
        setCurrentPage(1);
    };

    const [uniqueClients, setUniqueClients] = useState(['All']);

    // Pagination States
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const itemsPerPage = 25;

    const { items: sortedSnapshots, requestSort, sortConfig } = useSortableData(snapshots);
    const getSortIcon = (key) => {
        if (!sortConfig || sortConfig.key !== key) return <ArrowUpDown size={14} style={{ display: 'inline', marginLeft: '4px', opacity: 0.3 }} />;
        return sortConfig.direction === 'ascending' ? <ChevronUp size={14} style={{ display: 'inline', marginLeft: '4px', color: 'var(--primary)' }} /> : <ChevronDown size={14} style={{ display: 'inline', marginLeft: '4px', color: 'var(--primary)' }} />;
    };

    useEffect(() => {
        const fetchSnapshots = async () => {
            setLoading(true);
            try {
                const res = await api.inventory.getSnapshots(
                    currentPage,
                    itemsPerPage,
                    appliedFilters.searchTerm,
                    appliedFilters.selectedWarehouse,
                    appliedFilters.selectedDate,
                    appliedFilters.selectedClient,
                    appliedFilters.selectedDays,
                    appliedFilters.searchBySKU,
                    appliedFilters.searchByProduct
                ).catch(() => null);
                if (res?.data) {
                    setSnapshots(res.data.snapshots || []);
                    setChartData(res.data.chartData || []);
                    setTotalPages(res.data.totalPages || 0);
                    setTotalRecords(res.data.totalRecords || 0);
                    if (res.data.uniqueClients) setUniqueClients(res.data.uniqueClients);
                } else {
                    setSnapshots([]);
                    setChartData([]);
                }
            } catch (error) {
                console.error("Snapshots error", error);
            } finally {
                setLoading(false);
            }
        };

        fetchSnapshots();
    }, [currentPage, appliedFilters]);

    // Hardcode unique warehouses for the dropdown to avoid needing the 120k records locally
    const uniqueWarehouses = ['All', 'Primary', 'SC_Ramona', 'SC_DALLAS', 'SC_FRESNO', 'SC_Dubai', 'DRMTLGY', 'KITSCH_LA'];

    return (
        <div>
            <div className="page-header" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <h1>Daily Inventory Snapshots</h1>
                    <p>Historical record of End-of-Day inventory levels.</p>
                </div>
            </div>

            <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem 1.5rem' }}>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1', minWidth: '250px', position: 'relative' }}>
                        <div style={{ position: 'relative' }}>
                            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    width: '100%', padding: '0.6rem 1rem 0.6rem 2.5rem',
                                    borderRadius: '6px', border: '1px solid #e2e8f0',
                                    outline: 'none', fontFamily: 'inherit'
                                }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                                <input 
                                    type="checkbox" 
                                    checked={searchBySKU} 
                                    onChange={(e) => setSearchBySKU(e.target.checked)} 
                                /> SKU
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                                <input 
                                    type="checkbox" 
                                    checked={searchByProduct} 
                                    onChange={(e) => setSearchByProduct(e.target.checked)} 
                                /> Product
                            </label>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '200px' }}>
                        <Calendar size={18} color="var(--text-muted)" />
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            style={{
                                width: '100%', padding: '0.6rem 1rem',
                                borderRadius: '6px', border: '1px solid #e2e8f0',
                                outline: 'none', fontFamily: 'inherit',
                                backgroundColor: 'white'
                            }}
                        />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '200px' }}>
                        <Filter size={18} color="var(--text-muted)" />
                        <select
                            value={selectedClient}
                            onChange={(e) => setSelectedClient(e.target.value)}
                            style={{
                                width: '100%', padding: '0.6rem 1rem',
                                borderRadius: '6px', border: '1px solid #e2e8f0',
                                outline: 'none', fontFamily: 'inherit',
                                backgroundColor: 'white'
                            }}
                        >
                            {uniqueClients.map(c => (
                                <option key={c} value={c}>{c === 'All' ? 'All Clients' : c}</option>
                            ))}
                        </select>
                        <select
                            value={selectedDays}
                            onChange={(e) => setSelectedDays(Number(e.target.value))}
                            style={{
                                width: '100%', padding: '0.6rem 1rem',
                                borderRadius: '6px', border: '1px solid #e2e8f0',
                                outline: 'none', fontFamily: 'inherit',
                                backgroundColor: 'white'
                            }}
                        >
                            <option value={7}>Last 7 Days</option>
                            <option value={14}>Last 14 Days</option>
                            <option value={30}>Last 30 Days</option>
                            <option value={90}>Last 90 Days</option>
                        </select>
                        {user?.role !== 'manager' && (
                            <select
                                value={selectedWarehouse}
                                onChange={(e) => setSelectedWarehouse(e.target.value)}
                                style={{
                                    width: '100%', padding: '0.6rem 1rem',
                                    borderRadius: '6px', border: '1px solid #e2e8f0',
                                    outline: 'none', fontFamily: 'inherit',
                                    backgroundColor: 'white'
                                }}
                            >
                                {uniqueWarehouses.map(wh => (
                                    <option key={wh} value={wh}>{wh === 'All' ? 'All Warehouses' : wh}</option>
                                ))}
                            </select>
                        )}
                        <button
                            onClick={handleFetch}
                            style={{
                                padding: '0.6rem 1.5rem',
                                borderRadius: '6px',
                                border: 'none',
                                backgroundColor: 'var(--primary)',
                                color: 'white',
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                                marginLeft: '0.5rem',
                                minWidth: '100px'
                            }}
                        >
                            Fetch
                        </button>
                    </div>
                </div>
            </div>

            <div className="card" style={{ marginBottom: '2rem' }}>
                <h2 className="card-title">Overall Stock Volume by Day</h2>
                <div style={{ width: '100%', height: 350 }}>
                    <ResponsiveContainer>
                        <BarChart
                            data={chartData}
                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                            <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fill: 'var(--text-muted)' }} />
                            <YAxis tickLine={false} axisLine={false} tick={{ fill: 'var(--text-muted)' }} />
                            <Tooltip
                                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                                cursor={{ fill: 'rgba(0,0,0,0.02)' }}
                            />
                            <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle" />
                            <Bar dataKey="total_on_hand" name="Total On Hand" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="total_sellable" name="Total Sellable" fill="var(--accent)" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* ── Active Filters Summary ── */}
            {(appliedFilters.searchTerm || appliedFilters.selectedClient !== 'All' || appliedFilters.selectedWarehouse !== 'All' || appliedFilters.selectedDate || appliedFilters.selectedDays !== 7) && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    flexWrap: 'wrap', marginBottom: '1rem',
                    padding: '0.5rem 0.75rem',
                    background: 'rgba(79,70,229,0.04)',
                    borderRadius: '8px',
                    border: '1px solid rgba(79,70,229,0.12)'
                }}>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600, marginRight: '0.25rem' }}>
                        Active filters:
                    </span>
                    {appliedFilters.selectedWarehouse !== 'All' && user?.role !== 'manager' && (
                        <span style={{
                            fontSize: '0.78rem', fontWeight: 600,
                            background: 'rgba(79,70,229,0.1)', color: 'var(--primary)',
                            padding: '0.2rem 0.6rem', borderRadius: '999px',
                            border: '1px solid rgba(79,70,229,0.2)'
                        }}>🏭 Warehouse: {appliedFilters.selectedWarehouse}</span>
                    )}
                    {appliedFilters.selectedClient !== 'All' && (
                        <span style={{
                            fontSize: '0.78rem', fontWeight: 600,
                            background: 'rgba(16,185,129,0.1)', color: '#059669',
                            padding: '0.2rem 0.6rem', borderRadius: '999px',
                            border: '1px solid rgba(16,185,129,0.2)'
                        }}>👤 Client: {appliedFilters.selectedClient}</span>
                    )}
                    {appliedFilters.selectedDate && (
                        <span style={{
                            fontSize: '0.78rem', fontWeight: 600,
                            background: 'rgba(245,158,11,0.1)', color: '#d97706',
                            padding: '0.2rem 0.6rem', borderRadius: '999px',
                            border: '1px solid rgba(245,158,11,0.2)'
                        }}>📅 Date: {new Date(appliedFilters.selectedDate + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    )}
                    {appliedFilters.selectedDays !== 7 && (
                        <span style={{
                            fontSize: '0.78rem', fontWeight: 600,
                            background: 'rgba(59,130,246,0.1)', color: '#2563eb',
                            padding: '0.2rem 0.6rem', borderRadius: '999px',
                            border: '1px solid rgba(59,130,246,0.2)'
                        }}>⏱️ History: {appliedFilters.selectedDays} Days</span>
                    )}
                    {appliedFilters.searchTerm && (
                        <span style={{
                            fontSize: '0.78rem', fontWeight: 600,
                            background: 'rgba(239,68,68,0.08)', color: '#dc2626',
                            padding: '0.2rem 0.6rem', borderRadius: '999px',
                            border: '1px solid rgba(239,68,68,0.15)'
                        }}>🔍 Search: "{appliedFilters.searchTerm}"</span>
                    )}
                    <button
                        onClick={handleClearAll}
                        style={{
                            marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--text-muted)',
                            background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline'
                        }}
                    >Clear all</button>
                </div>
            )}

            <div className="card table-container">
                {loading ? (
                    <p>Loading snapshot data...</p>
                ) : (
                    <table className="responsive-table">
                        <thead>
                            <tr>
                                <th className="sortable" onClick={() => requestSort('date')}>Date {getSortIcon('date')}</th>
                                <th className="sortable" onClick={() => requestSort('warehouse_name')}>Fulfiller / Warehouse {getSortIcon('warehouse_name')}</th>
                                <th className="sortable" onClick={() => requestSort('sku')}>SKU {getSortIcon('sku')}</th>
                                <th className="sortable" onClick={() => requestSort('product_name')}>Product {getSortIcon('product_name')}</th>
                                <th className="sortable" style={{ textAlign: 'right' }} onClick={() => requestSort('start_count')}>Start Count {getSortIcon('start_count')}</th>
                                <th className="sortable" style={{ textAlign: 'right' }} onClick={() => requestSort('received')}>Received {getSortIcon('received')}</th>
                                <th style={{ textAlign: 'right' }}>In Transit</th>
                                <th className="sortable" style={{ textAlign: 'right' }} onClick={() => requestSort('picked')}>Picked {getSortIcon('picked')}</th>
                                <th style={{ textAlign: 'right' }}>Internal T</th>
                                <th className="sortable" style={{ textAlign: 'right' }} onClick={() => requestSort('spot_check')}>Spot Check {getSortIcon('spot_check')}</th>
                                <th className="sortable" style={{ textAlign: 'right' }} onClick={() => requestSort('restock')}>Restock {getSortIcon('restock')}</th>
                                <th style={{ textAlign: 'right' }}>Inventory Δ</th>
                                <th className="sortable" style={{ textAlign: 'right' }} onClick={() => requestSort('total_on_hand')}>End Count (On Hand) {getSortIcon('total_on_hand')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {snapshots.length === 0 ? (
                                <tr>
                                    <td colSpan="13" style={{ textAlign: 'center', padding: '4rem 2rem', color: 'var(--text-muted)' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                                            <Calendar size={48} style={{ opacity: 0.2 }} />
                                            <div>
                                                <p style={{ fontWeight: 500, color: 'var(--text-main)', marginBottom: '0.25rem' }}>No inventory snapshots found</p>
                                                <p style={{ fontSize: '0.875rem' }}>There are no historical end-of-day records matching your current filters.</p>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                sortedSnapshots.map((item, idx) => (
                                    <tr key={`${item.date}-${item.sku}-${item.warehouse_name}-${idx}`}>
                                        <td data-label="Date" style={{ color: 'var(--text-muted)', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                                            {new Date(item.date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                        </td>
                                        <td data-label="Warehouse" style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{item.warehouse_name || 'N/A'}</td>
                                        <td data-label="SKU" style={{ fontWeight: 700, color: 'var(--primary)', fontFamily: 'monospace', fontSize: '0.82rem' }}>{item.sku}</td>
                                        <td data-label="Product" style={{ fontSize: '0.85rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.product_name}>{item.product_name}</td>
                                        <td data-label="Start Count" style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{item.start_count ?? 0}</td>
                                        <td data-label="Received" style={{ textAlign: 'right', color: '#10b981', fontWeight: item.received > 0 ? 600 : 'normal' }}>+{item.received ?? 0}</td>
                                        <td data-label="In Transit" style={{ textAlign: 'right', color: 'var(--text-muted)' }}>0</td>
                                        <td data-label="Picked" style={{ textAlign: 'right', color: item.picked > 0 ? '#ef4444' : 'var(--text-muted)', fontWeight: item.picked > 0 ? 600 : 'normal' }}>
                                            {item.picked > 0 ? `-${item.picked}` : 0}
                                        </td>
                                        <td data-label="Internal T" style={{ textAlign: 'right', color: 'var(--text-muted)' }}>0</td>
                                        <td data-label="Spot Check" style={{ textAlign: 'right', color: item.spot_check !== 0 ? '#f59e0b' : 'var(--text-muted)', fontWeight: item.spot_check !== 0 ? 600 : 'normal' }}>{item.spot_check ?? 0}</td>
                                        <td data-label="Restock" style={{ textAlign: 'right', color: item.restock > 0 ? '#3b82f6' : 'var(--text-muted)', fontWeight: item.restock > 0 ? 600 : 'normal' }}>+{item.restock ?? 0}</td>
                                        <td data-label="Inventory Change" style={{ textAlign: 'right', color: 'var(--text-muted)' }}>0</td>
                                        <td data-label="End Count" style={{ textAlign: 'right', fontWeight: 700, color: 'var(--text-main)' }}>{item.total_on_hand}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                )}

                {!loading && totalRecords > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', backgroundColor: 'white', borderTop: '1px solid #e2e8f0', borderRadius: '0 0 8px 8px' }}>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalRecords)} of {totalRecords} entries
                        </div>
                            <Pagination currentPage={currentPage} totalPages={totalPages} setCurrentPage={setCurrentPage} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default Snapshots;
